/* eslint-disable no-console */

import {
  BIWEEKLY,
  DAILY,
  DOES_NOT_REPEAT,
  EVERY_SIX_MONTHS,
  EVERY_THREE_MONTHS,
  MONTHLY,
  WEEKLY,
  YEARLY,
} from '/enum/reminders.enum';

export interface ReminderData {
  startDate: string | Date | null;
  time?: string | Date;
  frequency: string;
  endDate?: string | Date | null;
  checkState?: boolean;
  checkDate?: string;
  formattedStartDate?: string;
  formattedTime?: string;
  formattedEndDate?: string;
  reminderData?: Record<string, unknown>;
  candidateId?: string;
  timezone?: string;
}
export const repeatArray = [
  DAILY,
  WEEKLY,
  BIWEEKLY,
  MONTHLY,
  EVERY_THREE_MONTHS,
  EVERY_SIX_MONTHS,
  YEARLY,
  DOES_NOT_REPEAT,
] as const;

export const currentDate = () => new Date();

export const isToday = (selectedDate: Date): boolean => {
  if (selectedDate) {
    return (
      selectedDate.getFullYear() === currentDate().getFullYear() &&
      selectedDate.getMonth() === currentDate().getMonth() &&
      selectedDate.getDate() === currentDate().getDate()
    );
  }
  return false;
};

export const validateTime = (
  selectedDate: Date,
  selectedTime: Date,
): boolean => {
  if (isToday(selectedDate) && selectedTime < currentDate()) {
    return false;
  }
  return true;
};

export const formatDate = (date: Date): string => {
  if (date) {
    return date?.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }
};

export const formatTime = (date: string | null | undefined | Date): string => {
  if (!date) return '';
  const dateStr = date instanceof Date ? date.toISOString() : date;
  const dateObj = new Date(dateStr);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj?.toLocaleTimeString('en-US', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Utility function to get a date without the time component
export const getDateWithoutTime = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getNextReminderDate = (
  date: string,
  repeat: string,
  isEndDateValidation: boolean = false,
): Date => {
  let nextDate = new Date(date);
  nextDate = resetToMidnight(nextDate);
  const currentDateMidNight = resetToMidnight(new Date());

  const incrementDateByFrequency = (date: Date, frequency: string): Date => {
    switch (frequency) {
      case DAILY:
        date.setDate(date.getDate() + 1);
        break;
      case WEEKLY:
        date.setDate(date.getDate() + 7);
        break;
      case BIWEEKLY:
        date.setDate(date.getDate() + 14);
        break;
      case MONTHLY:
        date.setMonth(date.getMonth() + 1);
        break;
      case EVERY_THREE_MONTHS:
        date.setMonth(date.getMonth() + 3);
        break;
      case EVERY_SIX_MONTHS:
        date.setMonth(date.getMonth() + 6);
        break;
      case YEARLY:
        date.setFullYear(date.getFullYear() + 1);
        break;
      case DOES_NOT_REPEAT:
        return date;
      default:
        return date;
    }
    return date;
  };
  // If nextDate is already in the future, increment it once based on frequency
  if (nextDate > currentDateMidNight && isEndDateValidation) {
    nextDate = incrementDateByFrequency(nextDate, repeat);
  }
  // If nextDate is in the past or current date, keep incrementing until it's in the future
  let previousDate = nextDate;
  while (nextDate <= currentDateMidNight) {
    nextDate = incrementDateByFrequency(nextDate, repeat);
    // to avoid infinite loop
    if (nextDate.getTime() === previousDate.getTime()) {
      break;
    }
  }
  return nextDate;
};

export const toLocalDate = (input: Date | string): string => {
  const date = input instanceof Date ? input : new Date(input);

  // Check if the date is invalid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date provided');
    return '';
  }

  // Extract local year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Set the next reminder date to 12:00 AM
export const resetToMidnight = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0); // Set time to 12:00 AM
  return newDate;
};

export function getRepeatText(frequency: string) {
  switch (frequency) {
    case DAILY:
      return 'Repeats every day';
    case WEEKLY:
      return 'Repeats every week';
    case BIWEEKLY:
      return 'Repeats every 2 weeks';
    case MONTHLY:
      return 'Repeats every month';
    case EVERY_THREE_MONTHS:
      return 'Repeats every 3 months';
    case EVERY_SIX_MONTHS:
      return 'Repeats every 6 months';
    case YEARLY:
      return 'Repeats every year';
    case DOES_NOT_REPEAT:
      return 'Does not repeat';
    default:
      return `Repeats ${frequency}`;
  }
}

export const validateEndDateForFrequency = (
  startDate: Date,
  endDate: Date,
  frequency: string,
): {isValid: boolean; minDate: Date} => {
  const formattedEndDate = resetToMidnight(endDate);
  const startDateAdjusted = resetToMidnight(startDate);
  const minDate = getNextReminderDate(
    startDateAdjusted.toISOString(),
    frequency,
    true,
  );
  return {
    isValid: formattedEndDate >= minDate,
    minDate,
  };
};
/**
 * Combines a date and time, applies the timezone offset, and returns a formatted string.
 * @param date - Date object representing the date.
 * @param time - Date object representing the time.
 * @returns Formatted string in the format "yyyy-MM-dd HH:mm:ss+00:00"
 */
export const formatDateWithTimezoneOffset = (
  date: Date,
  time: Date,
): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = time.getHours().toString().padStart(2, '0');
  const minutes = time.getMinutes().toString().padStart(2, '0');
  const seconds = time.getSeconds().toString().padStart(2, '0');
  const combinedDateTime = new Date(
    `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`,
  );

  // Calculate the timezone offset in hours and minutes
  const timezoneOffsetMinutes = combinedDateTime.getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(timezoneOffsetMinutes / 60))
    .toString()
    .padStart(2, '0');
  const offsetMinutes = Math.abs(timezoneOffsetMinutes % 60)
    .toString()
    .padStart(2, '0');
  const offsetSign = timezoneOffsetMinutes > 0 ? '-' : '+';

  // Format the date with timezone offset
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

/**
 * Converts a given date and time (in 12-hour format) to an ISO 8601 formatted UTC string.
 *
 * @param {Date} date - The date to be used, which will be combined with the provided time.
 * @param {string} time - The time in 12-hour format (e.g., "2:30 PM") that needs to be converted to 24-hour format.
 * @returns {string} - The formatted ISO 8601 string in UTC (e.g., "YYYY-MM-DDTHH:mm:ss+00:00").
 *
 * This function extracts the date from the provided `Date` object, converts the time to a 24-hour format,
 * and combines both to create a UTC ISO string. If the time format is invalid, it returns an empty string.
 */
export const formatToUTCISO = (date: Date, time: string): string => {
  try {
    // Extract the date part (YYYY-MM-DD)
    const datePart = date.toISOString().split('T')[0];
    // Convert the time from 12-hour to 24-hour format
    const time24h = convertTime12to24(time);
    if (!time24h) {
      throw new Error('Invalid time format');
    }
    // Combine the date part and the converted time into a valid ISO string format
    const dateTimeStr = `${datePart}T${time24h}:00Z`; // Adding seconds (00) for completeness

    const dateTime = new Date(dateTimeStr); // Create the Date object

    // Ensure we are working with UTC time
    const hours = dateTime.getUTCHours();
    const minutes = dateTime.getUTCMinutes();
    const seconds = dateTime.getUTCSeconds();

    // Return formatted ISO string in UTC
    const formattedISO = `${datePart}T${String(hours).padStart(
      2,
      '0',
    )}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0',
    )}+00:00`;

    return formattedISO;
  } catch (error) {
    return ''; // Return an empty string in case of error
  }
};

/**
 * Converts a 12-hour formatted time string (e.g., "2:30 PM") to a 24-hour format (e.g., "14:30").
 *
 * @param {any} time12h - The time string in 12-hour format, which may include non-breaking spaces.
 * @returns {string} - The time converted to 24-hour format (e.g., "HH:mm").
 */
const convertTime12to24 = (time12h: any) => {
  const cleanedTime12h = time12h?.replace(/\u202F/g, ' ').trim();
  const [time, modifier] = cleanedTime12h.split(' ');
  let [hours, minutes] = time.split(':');

  if (hours === '12' && modifier === 'AM') {
    hours = '00';
  }

  if (modifier === 'PM' && hours !== '12') {
    hours = (parseInt(hours, 10) + 12).toString();
  }

  const formattedHours = parseInt(hours, 10).toString().padStart(2, '0');
  return `${formattedHours}:${minutes}`;
};

/**
 * Converts ISO date and time inputs into a formatted date and time string in AM/PM format.
 *
 * @param {string | Date} isoDate - The ISO date string or Date object to be converted.
 * @param {string | Date} isoTime - The ISO time string or Date object to be converted.
 * @returns {object} - An object containing:
 *   - `formattedDate`: The formatted date string (e.g., "Wed, Oct 25, 2023").
 *   - `formattedTime`: The formatted time string in 12-hour AM/PM format (e.g., "2:30 PM").
 *   - `combinedDate`: A combined date-time object in ISO string format, or `null` if inputs are invalid.
 */
export const convertISOtoDateTimeAMPM = (
  isoDate: string | Date,
  isoTime: string | Date,
) => {
  if (!isoDate || !isoTime) {
    return {
      formattedDate: 'Invalid date',
      formattedTime: 'Invalid time',
      combinedDate: null,
    };
  }

  // Create Date objects
  const date = typeof isoDate === 'string' ? new Date(isoDate) : isoDate;
  const time = typeof isoTime === 'string' ? new Date(isoTime) : isoTime;

  // Validate dates
  if (isNaN(date.getTime()) || isNaN(time.getTime())) {
    return {
      formattedDate: 'Invalid date',
      formattedTime: 'Invalid time',
      combinedDate: null,
    };
  }

  // Extract components for date and time
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  const hours = time.getHours();
  const minutes = time.getMinutes();

  // Combine into a single Date object
  const combinedDate = new Date(year, month, day, hours, minutes);

  // Convert combinedDate to AM/PM format
  const hours12 = combinedDate.getHours() % 12 || 12; // 12-hour format
  const ampm = combinedDate.getHours() >= 12 ? 'PM' : 'AM';
  const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;
  const formattedTime = `${hours12}:${minutesFormatted} ${ampm}`;

  // Format the date for display
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  } as const;
  const formattedDate = combinedDate.toLocaleDateString('en-US', options);

  // Return all values
  return {
    formattedDate,
    formattedTime,
    combinedDate: combinedDate.toISOString(), // Use ISO format to match API
  };
};

/**
 * Converts a Date object to ISO 8601 format with UTC offset (e.g., 2024-11-30T02:31:00+00:00)
 */
export const formatDateToISOWithOffset = (date: Date): string => {
  const pad = (num: number) => String(num).padStart(2, '0');
  // Get UTC components
  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1); // Months are 0-indexed
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());

  // Construct ISO string with offset
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
};

export const convertToLocalISO = (isoDate: string | Date): string => {
  const date = new Date(isoDate);

  // Ensure the date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }
  // Get the timezone offset in minutes and adjust the date accordingly
  const timezoneOffset = date.getTimezoneOffset(); // in minutes
  date.setMinutes(date.getMinutes() - timezoneOffset); // Adjust to local time

  return date.toISOString(); // Return the ISO format with the local timezone applied
};

/**
 * Determines whether a checkbox should be shown based on reminder dates, check state, and frequency.
 *
 * @param {Date | undefined} reminderDate - The start date of the reminder.
 * @param {Date | undefined} nextReminderDate - The next reminder date.
 * @param {boolean} checkState - Whether the reminder is currently checked.
 * @param {Date | undefined} checkDate - The date the reminder was last checked.
 * @param {string} frequency - The frequency of the reminder.
 * @returns {boolean} - Returns true if the checkbox should be shown, otherwise false.
 */

export const shouldShowCheckbox = (
  reminderDate: Date | null,
  nextReminderDate: Date | null,
  checkDate: Date | null,
  frequency: string,
  isChecked: boolean,
  endDate: Date | null,
): boolean => {
  const today = toLocalDate(new Date());
  const startOfReminder = reminderDate ? toLocalDate(reminderDate) : null;
  const nextOfReminder = nextReminderDate
    ? toLocalDate(nextReminderDate)
    : null;
  const checkDateLocal = checkDate ? toLocalDate(checkDate) : null;
  const endOfReminder = endDate ? toLocalDate(endDate) : null;

  // If reminderDate is in the future, do not show the checkbox.
  if (startOfReminder && startOfReminder > today) {
    return false;
  }

  // If frequency is "Does Not Repeat", always show the checkbox.
  if (frequency === DOES_NOT_REPEAT) {
    return true;
  }

  // If `endDate` exists and today is beyond `endDate`, hide the checkbox.
  if (endOfReminder && today > endOfReminder) {
    return !isChecked;
  }

  // From `reminderDate` until `endOfReminder`.
  if (
    startOfReminder &&
    endOfReminder &&
    today >= startOfReminder &&
    today <= endOfReminder
  ) {
    // If today is equal to `startOfReminder` or `nextOfReminder`, show checkbox based on `isChecked`.
    if (today === startOfReminder || today === nextOfReminder) {
      return !isChecked;
    }

    // If `checkDateLocal` is within the range of `reminderDate` and `nextReminderDate`, hide the checkbox.
    if (
      checkDateLocal &&
      startOfReminder &&
      nextOfReminder &&
      checkDateLocal > startOfReminder &&
      checkDateLocal < nextOfReminder
    ) {
      return false;
    }

    // If `checkDateLocal` is in the past (before `startOfReminder`), show the checkbox.
    if (checkDateLocal && checkDateLocal < startOfReminder) {
      return true;
    }
  }

  // Default behavior for frequencies other than `DOES_NOT_REPEAT`:
  // Show the checkbox if unchecked, otherwise hide it.
  return !isChecked;
};

/**
 * Determines whether a reminder time has passed based on the reminder and next reminder dates,
 * frequency, and whether the reminder has been checked.
 *
 * @param {string | null} reminderDate - The reminder date in ISO string format.
 * @param {string | null} nextReminderDate - The next reminder date in ISO string format.
 * @param {string} frequency - The frequency of the reminder (e.g., "Does Not Repeat").
 * @param {boolean} isChecked - Whether the reminder has been checked.
 * @param {string | null} checkDate - The date the reminder was last checked.
 * @returns {boolean} - Returns true if the reminder time has passed, otherwise false.
 */
export const isReminderTimePassed = (
  reminderDate: string | null,
  nextReminderDate: string | null,
  frequency: string,
  isChecked: boolean,
  checkDate: string | null,
): boolean => {
  // Current time in UTC
  const now = new Date(); // This is already in UTC format
  const nowUTC = now.toISOString();

  // Convert reminderDate, nextReminderDate, and checkDate to Date objects
  const reminder = reminderDate ? new Date(reminderDate) : null;
  const nextReminder = nextReminderDate ? new Date(nextReminderDate) : null;
  const lastCheckDate = checkDate ? new Date(checkDate) : null;

  // Convert all to UTC strings
  const reminderUTC = reminder ? reminder.toISOString() : null;
  const nextReminderUTC = nextReminder ? nextReminder.toISOString() : null;
  const lastCheckDateUTC = lastCheckDate ? lastCheckDate.toISOString() : null;

  // Case 1: No repetition (reminder doesn't repeat)
  if (frequency === DOES_NOT_REPEAT) {
    if (reminderUTC) {
      return nowUTC > reminderUTC;
    }
    return false; // If reminderUTC is null, time hasn't passed
  }
  // Case 2: Repeated reminders
  const isWithinReminderPeriod = reminderUTC
    ? reminderUTC <= nowUTC && (!nextReminderUTC || nowUTC < nextReminderUTC)
    : false;

  if (!isChecked) {
    // If not checked, remind if the current time has passed the reminder time
    return reminderUTC ? nowUTC > reminderUTC : false;
  }

  if (isChecked && lastCheckDateUTC) {
    // Check if last check date is within the reminder period
    const isCheckedWithinPeriod =
      isWithinReminderPeriod &&
      reminderUTC &&
      lastCheckDateUTC >= reminderUTC &&
      (!nextReminderUTC || lastCheckDateUTC < nextReminderUTC);

    // Return whether reminder time has passed or not
    return !isCheckedWithinPeriod; // Reminder passed if not checked within the period
  }

  return false; // Default to false if no conditions match
};

export const getTimezoneOffsetInHours = () => {
  // getTimezoneOffset() returns the offset in minutes, so we divide by 60 to convert it to hours
  const offsetInMinutes = new Date().getTimezoneOffset();
  const offsetInHours = -offsetInMinutes / 60; // Adjust the sign because getTimezoneOffset returns negative values for time zones ahead of UTC
  return offsetInHours;
};

export const combineDateWithTime = (
  isoDate: Date | null,
  timeString: string,
  useUTC: boolean = false,
): Date | null => {
  if (!isoDate || !timeString) {
    return null;
  }

  // Parse the time string
  const timeParts = timeString.match(/(\d+):(\d+)\s?(AM|PM)/i);
  if (!timeParts || timeParts.length < 4) {
    console.error('Invalid time format. Expected format: "hh:mm AM/PM"');
    return null;
  }

  const hours = parseInt(timeParts[1] ?? '0', 10);
  const minutes = parseInt(timeParts[2] ?? '0', 10);
  const period = timeParts[3]?.toUpperCase();

  if (!period) {
    console.error('Invalid time format. Expected "AM" or "PM"');
    return null;
  }

  // Convert to 24-hour format
  const adjustedHours =
    period === 'PM' && hours !== 12
      ? hours + 12
      : hours === 12 && period === 'AM'
      ? 0
      : hours;

  let combinedDate: Date;

  if (useUTC) {
    // Handle everything in UTC by adjusting the time
    // We need to account for the timezone offset for CST (UTC-6)
    const offsetInHours = getTimezoneOffsetInHours(); // CST offset from UTC
    const utcAdjustedHours = adjustedHours - offsetInHours;

    combinedDate = new Date(
      Date.UTC(
        isoDate.getUTCFullYear(),
        isoDate.getUTCMonth(),
        isoDate.getUTCDate(),
        utcAdjustedHours,
        minutes,
        0,
        0,
      ),
    );
  } else {
    // Handle everything in local time zone
    combinedDate = new Date(isoDate);
    combinedDate.setHours(adjustedHours, minutes, 0, 0);
  }
  return combinedDate;
};
